import { useState, useRef, useCallback, useEffect, useId } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { SectionLoadError } from "./SectionErrorBoundary";
import { useStrategicSupplierPercentage } from "@/hooks/useInvoices";
import { useQueryClient } from "@tanstack/react-query";

const TIERS = [
  {
    key: "untapped" as const,
    label: "Untapped Potential",
    min: 0,
    max: 39,
    color: "#ef4444",
    tooltip:
      "There's a big opportunity to improve here. Right now, too much business is being placed outside of our strongest supplier partnerships. Moving more sales to strategic suppliers can make a real difference in your earnings, support, and client experience.",
  },
  {
    key: "opportunity" as const,
    label: "Opportunity for Improvement",
    min: 40,
    max: 64,
    color: "#f59e0b",
    tooltip:
      "You're leaving value on the table. Increasing your strategic supplier mix can lead to better commission opportunities, stronger supplier support, and more benefits for you and your clients.",
  },
  {
    key: "good" as const,
    label: "Good",
    min: 65,
    max: 74,
    color: "#84cc16",
    tooltip:
      "You're doing well and are close to excellent. Increasing your strategic supplier mix a bit more can help you maximize commissions, access additional benefits, and strengthen supplier support for you and your clients.",
  },
  {
    key: "excellent" as const,
    label: "Excellent!",
    min: 75,
    max: 100,
    color: "#10b981",
    tooltip:
      "You're operating at an excellent level and getting the most from our strategic supplier partnerships. Keep it up!",
  },
] as const;

function getTier(percentage: number) {
  for (const tier of TIERS) {
    if (percentage >= tier.min && percentage <= tier.max) return tier;
  }
  return TIERS[0];
}

function getNextTier(percentage: number) {
  const currentTier = getTier(percentage);
  const idx = TIERS.indexOf(currentTier);
  return idx < TIERS.length - 1 ? TIERS[idx + 1] : null;
}

const SUPPLIER_LISTING_URL = "https://worldvia.pro/pro-suppliers";

/** SVG semi-circular gauge with colored zones and a needle. */
function GaugeSvg({
  percentage,
  hoveredZone,
  onZoneHover,
  onZoneLeave,
}: {
  percentage: number;
  hoveredZone: string | null;
  onZoneHover: (key: string) => void;
  onZoneLeave: () => void;
}) {
  const cx = 90;
  const cy = 85;
  const r = 70;
  const strokeWidth = 14;

  const percentToAngle = (pct: number) => {
    const clamped = Math.max(0, Math.min(100, pct));
    return 180 + (clamped / 100) * 180;
  };

  const describeArc = (startPct: number, endPct: number) => {
    const startAngle = percentToAngle(startPct);
    const endAngle = percentToAngle(endPct);
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const needleAngle = percentToAngle(percentage);
  const needleRad = (needleAngle * Math.PI) / 180;
  const needleLength = r - strokeWidth / 2 - 4;
  const needleX = cx + needleLength * Math.cos(needleRad);
  const needleY = cy + needleLength * Math.sin(needleRad);

  const tier = getTier(percentage);

  const zones = [
    { ...TIERS[0], startPct: 0, endPct: 40 },
    { ...TIERS[1], startPct: 40, endPct: 65 },
    { ...TIERS[2], startPct: 65, endPct: 75 },
    { ...TIERS[3], startPct: 75, endPct: 100 },
  ];

  return (
    <svg
      viewBox="0 0 180 105"
      className="w-full max-w-[200px]"
      role="img"
      aria-label={`Strategic Supplier Percentage: ${percentage}%. Rating: ${tier.label}`}
    >
      {zones.map((zone) => {
        const isHovered = hoveredZone === zone.key;
        const increaseNeeded =
          percentage < zone.min ? zone.min - percentage : null;

        return (
          <g key={zone.key}>
            <path
              d={describeArc(zone.startPct, zone.endPct)}
              fill="none"
              stroke={zone.color}
              strokeWidth={isHovered ? strokeWidth + 4 : strokeWidth}
              strokeLinecap="butt"
              opacity={isHovered ? 1 : 0.35}
              className="transition-all duration-200"
              onMouseEnter={() => onZoneHover(zone.key)}
              onMouseLeave={onZoneLeave}
              onTouchStart={() => onZoneHover(zone.key)}
              onTouchEnd={onZoneLeave}
              style={{ cursor: "pointer" }}
            />
            {isHovered && increaseNeeded !== null && (
              <text
                x={cx}
                y={cy - r - 8}
                textAnchor="middle"
                className="fill-current text-foreground"
                fontSize="8"
                fontWeight="600"
              >
                +{increaseNeeded}% to {zone.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Active arc overlay */}
      <path
        d={describeArc(0, percentage)}
        fill="none"
        stroke={tier.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={0.95}
      />

      {/* Needle */}
      <line
        x1={cx}
        y1={cy}
        x2={needleX}
        y2={needleY}
        stroke="#374151"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r="4" fill="#374151" />

      {/* Percentage text */}
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        fontSize="22"
        fontWeight="700"
        className="fill-current text-foreground"
      >
        {percentage}%
      </text>
    </svg>
  );
}

function TooltipPopover({
  id,
  tier,
  isOpen,
  triggerRef,
  onClose,
}: {
  id: string;
  tier: (typeof TIERS)[number];
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      id={id}
      ref={popoverRef}
      role="tooltip"
      className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg text-xs leading-relaxed"
    >
      <p className="font-semibold mb-1" style={{ color: tier.color }}>
        {tier.label}
      </p>
      <p>{tier.tooltip}</p>
      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-border" />
    </div>
  );
}

export function StrategicSupplierGauge({ tramsAccountId, viewMode }: { tramsAccountId?: number; viewMode?: "my" | "all" } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useStrategicSupplierPercentage({ tramsAccountId, viewMode });
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const labelRef = useRef<HTMLAnchorElement>(null);
  const tooltipId = useId();

  const handleZoneHover = useCallback((key: string) => setHoveredZone(key), []);
  const handleZoneLeave = useCallback(() => setHoveredZone(null), []);
  const closeTooltip = useCallback(() => setTooltipOpen(false), []);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Strategic Supplier Percentage"
        error={error}
        onRetry={() =>
          queryClient.invalidateQueries({
            queryKey: ["/api/invoices/strategic-supplier-percentage"],
          })
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center px-3 py-2">
        <Skeleton className="h-[80px] w-[140px] rounded-t-full mb-2" />
        <Skeleton className="h-3 w-24" />
      </div>
    );
  }

  if (!data?.hasActivity) {
    return (
      <div
        className="flex flex-col items-center justify-center text-center px-3 py-2 max-w-[220px]"
        role="status"
        aria-label="No strategic supplier activity"
      >
        <p className="text-xs text-muted-foreground leading-relaxed">
          No invoiced activity in the past 90 days.
        </p>
        <a
          href={SUPPLIER_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 mt-1"
        >
          Start building momentum with strategic suppliers.
        </a>
      </div>
    );
  }

  const percentage = data.percentage;
  const tier = getTier(percentage);
  const nextTier = getNextTier(percentage);

  return (
    <div
      className="relative flex flex-col items-center justify-center px-3 py-1"
      role="region"
      aria-label="Strategic Supplier Percentage"
    >
      {/* Tier label */}
      <span
        className="text-xs font-semibold mb-0.5"
        style={{ color: tier.color }}
        aria-live="polite"
      >
        {tier.label}
      </span>

      {/* Gauge */}
      <GaugeSvg
        percentage={percentage}
        hoveredZone={hoveredZone}
        onZoneHover={handleZoneHover}
        onZoneLeave={handleZoneLeave}
      />

      {/* Linked label with tooltip */}
      <div className="relative flex max-w-full flex-wrap items-center justify-center gap-1">
        <a
          ref={labelRef}
          href={SUPPLIER_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          onBlur={() => setTooltipOpen(false)}
          onClick={() => setTooltipOpen((p) => !p)}
          aria-describedby={tooltipOpen ? tooltipId : undefined}
          className="relative rounded px-1 text-xs font-semibold text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors before:absolute before:inset-x-0 before:-inset-y-3 before:content-[''] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:before:content-none"
        >
          Strategic Supplier Percentage
        </a>
        <InfoHint label="How strategic supplier percentage is calculated">
          <p>
            Percent of invoiced sales from strategic suppliers over the last 90
            days.
          </p>
          <KpiBasisNote card="strategicSupplierPercentage" />
        </InfoHint>
        <TooltipPopover
          id={tooltipId}
          tier={tier}
          isOpen={tooltipOpen}
          triggerRef={labelRef}
          onClose={closeTooltip}
        />
      </div>

      {/* Next tier nudge on hover */}
      {nextTier && hoveredZone === null && (
        <p className="text-xs text-muted-foreground/60 mt-0.5">
          +{nextTier.min - percentage}% to reach{" "}
          <span className="italic" style={{ color: nextTier.color }}>{nextTier.label}</span>
        </p>
      )}
    </div>
  );
}
